import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, CalendarDays } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAvailableSlots, useBookSessionDirect } from "@/lib/sessionQueries";
import AddSessionsModal from "./AddSessionsModal";

/**
 * Master v5 Prompt 32 — Client 360 -> Sessions -> "Book Session". Coach
 * and client already agreed a time outside the app; this loads REAL
 * TidyCal availability, lets the coach pick it directly, and books it
 * immediately as a 'booked' Session — no proposal, no client
 * acceptance step (see session_repository.book_session_directly).
 *
 * Zero-credit handling: if the backend replies 402 ("no session
 * credits"), the existing "+ Add sessions" modal opens ON TOP, inline
 * — never closing this modal or losing the picked slot/name/email —
 * and once credits are added, the exact same booking is retried
 * automatically. This is the ONE place that check happens (also
 * covers the rare race where credits ran out between opening this
 * modal and confirming).
 */
export default function BookSessionModal({ workspaceId, memberId, memberName, memberEmail, workspaceMembers, accessType, onClose }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [clientName, setClientName] = useState(memberName || "");
  const [clientEmail, setClientEmail] = useState(memberEmail || "");
  const [showAddCredits, setShowAddCredits] = useState(false);

  const rangeStartsAt = useMemo(() => new Date().toISOString(), []);
  const rangeEndsAt = useMemo(() => new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(), []);
  const { data, isLoading, isError } = useAvailableSlots(workspaceId, { startsAt: rangeStartsAt, endsAt: rangeEndsAt });
  const bookSession = useBookSessionDirect(workspaceId, memberId);

  const slotsByDate = useMemo(() => {
    const groups = {};
    for (const slot of data?.slots || []) {
      const day = new Date(slot.starts_at).toDateString();
      (groups[day] = groups[day] || []).push(slot);
    }
    return groups;
  }, [data]);
  const dateKeys = Object.keys(slotsByDate);

  const canConfirm = !!selectedSlot && clientName.trim().length > 0 && clientEmail.trim().length > 2;

  async function handleConfirm() {
    if (!canConfirm) return;
    try {
      await bookSession.mutateAsync({ starts_at: selectedSlot.starts_at, client_name: clientName.trim(), client_email: clientEmail.trim() });
      toast.success(`Session booked — ${new Date(selectedSlot.starts_at).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}`);
      onClose();
    } catch (err) {
      if (err.status === 402) {
        setShowAddCredits(true);
        return;
      }
      // Master v5 Prompt 32 QA fix — never surface the raw TidyCal/
      // endpoint detail to the coach; keep it in the console for
      // debugging only.
      console.error("Book Session failed:", err.body?.detail || err.message);
      toast.error("Couldn't confirm this time with TidyCal. Please try a different slot or try again shortly.");
    }
  }

  return (
    <>
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-2xl" data-testid="book-session-modal">
          <DialogHeader>
            <DialogTitle className="font-serif">Book session</DialogTitle>
            <DialogDescription>
              Pick a real available time — this books it directly, with no proposal or acceptance step.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {isLoading && (
              <div className="flex items-center gap-2 text-sm text-ink-soft py-6 justify-center" data-testid="book-session-slots-loading">
                <Loader2 size={16} className="animate-spin" /> Loading real availability…
              </div>
            )}
            {isError && (
              <p className="text-sm text-red-600 py-2" data-testid="book-session-slots-error">
                Couldn't load available times from TidyCal. Try again in a moment.
              </p>
            )}
            {!isLoading && !isError && dateKeys.length === 0 && (
              <p className="text-sm text-ink-soft py-2" data-testid="book-session-slots-empty">No available times in the next 14 days.</p>
            )}

            {dateKeys.length > 0 && (
              <div>
                <label className="text-xs text-ink-soft uppercase mb-1.5 block flex items-center gap-1.5">
                  <CalendarDays size={13} /> Date
                </label>
                <div className="flex flex-wrap gap-1.5" data-testid="book-session-date-chips">
                  {dateKeys.map((day) => (
                    <button
                      key={day}
                      onClick={() => { setSelectedDate(day); setSelectedSlot(null); }}
                      className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${selectedDate === day ? "bg-terracotta text-paper border-terracotta" : "border-ink/15 text-ink-soft hover:border-terracotta/50"}`}
                      data-testid={`book-session-date-chip-${day.replace(/\s/g, "-")}`}
                    >
                      {new Date(day).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedDate && (
              <div>
                <label className="text-xs text-ink-soft uppercase mb-1.5 block">Time</label>
                <div className="flex flex-wrap gap-1.5" data-testid="book-session-time-chips">
                  {slotsByDate[selectedDate].map((slot) => (
                    <button
                      key={slot.starts_at}
                      onClick={() => setSelectedSlot(slot)}
                      className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${selectedSlot?.starts_at === slot.starts_at ? "bg-terracotta text-paper border-terracotta" : "border-ink/15 text-ink-soft hover:border-terracotta/50"}`}
                      data-testid={`book-session-time-chip-${slot.starts_at}`}
                    >
                      {new Date(slot.starts_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedSlot && (
              <div className="border border-ink/10 rounded-lg p-4 space-y-3" data-testid="book-session-client-details">
                <p className="text-sm font-medium text-ink">
                  {new Date(selectedSlot.starts_at).toLocaleString("en-US", { weekday: "long", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </p>
                <div>
                  <label className="text-xs text-ink-soft uppercase mb-1.5 block">Client name</label>
                  <Input value={clientName} onChange={(e) => setClientName(e.target.value)} data-testid="book-session-client-name-input" />
                </div>
                <div>
                  <label className="text-xs text-ink-soft uppercase mb-1.5 block">Client email</label>
                  <Input value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} data-testid="book-session-client-email-input" />
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="text-sm text-ink-soft px-4 py-2 rounded-md hover:bg-ink/5" data-testid="book-session-cancel-btn">Cancel</button>
            <button
              onClick={handleConfirm}
              disabled={!canConfirm || bookSession.isPending}
              className="bg-terracotta text-paper text-sm font-medium px-4 py-2 rounded-md hover:bg-terracotta/90 disabled:opacity-50"
              data-testid="book-session-confirm-btn"
            >
              {bookSession.isPending ? "Booking…" : "Book session"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {showAddCredits && (
        <AddSessionsModal
          workspaceId={workspaceId}
          memberId={memberId}
          accessType={accessType}
          workspaceMembers={workspaceMembers}
          onClose={() => setShowAddCredits(false)}
          onSuccess={() => { setShowAddCredits(false); handleConfirm(); }}
          nested
        />
      )}
    </>
  );
}
